import { useState } from "react";
import { FileText, Loader2 } from "lucide-react";

const IMAGE_URL_PATTERN = /\.(jpe?g|png|gif|webp|bmp|heic)(\?|#|$)/i;
const PDF_VIEWER_PARAMS = "navpanes=0&view=FitH";

const getPdfViewerUrl = (url: string) => (url.includes("#") ? url : `${url}#${PDF_VIEWER_PARAMS}`);

export interface IDocumentFile {
  key: string;
  label: string;
  url: string;
}

interface IDocumentViewerProps {
  document?: IDocumentFile;
  emptyState?: { title: string; description: string };
}

const DocumentViewer = ({ document, emptyState }: IDocumentViewerProps) => {
  const [loadedKey, setLoadedKey] = useState<string | null>(null);

  if (!document?.url) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 h-full px-6 text-center">
        <FileText className="w-8 h-8 text-gray-300" aria-hidden="true" />
        <p className="text-sm font-medium text-gray-700">{emptyState?.title ?? "No report attached yet"}</p>
        <p className="text-xs text-gray-500">
          {emptyState?.description ?? "Reports appear here once the lab uploads them."}
        </p>
      </div>
    );
  }

  const isLoading = loadedKey !== document.key;
  const isImage = IMAGE_URL_PATTERN.test(document.url);

  return (
    <div className="relative h-full bg-white">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center gap-2 bg-white text-xs text-gray-500">
          <Loader2 className="w-4 h-4 motion-safe:animate-spin" aria-hidden="true" />
          Loading {document.label.toLowerCase()}
        </div>
      )}
      {isImage ? (
        <div className="h-full overflow-auto bg-gray-50">
          <img
            key={document.key}
            src={document.url}
            alt={document.label}
            onLoad={() => setLoadedKey(document.key)}
            onError={() => setLoadedKey(document.key)}
            className="mx-auto max-w-full h-auto"
          />
        </div>
      ) : (
        <iframe
          key={document.key}
          title={document.label}
          src={getPdfViewerUrl(document.url)}
          onLoad={() => setLoadedKey(document.key)}
          className="w-full h-full border-0"
        />
      )}
    </div>
  );
};

export default DocumentViewer;
